import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { DEFAULT_PROJECT_COLOR, DEFAULT_PROJECT_ICON } from "@/lib/projectIcons";
import { ProjectIconPicker } from "./ProjectIconPicker";

/** Mirrors tern_domain::project_key::suggest. */
export function suggestKey(name: string): string {
  const words = name.split(/[^A-Za-z0-9]+/).filter(Boolean);
  let key = "";
  if (words.length >= 2) key = words.slice(0, 4).map((w) => w[0]!.toUpperCase()).join("");
  else if (words[0]) key = words[0].slice(0, 3).toUpperCase();
  if (/^\d/.test(key)) key = "P" + key;
  return key.slice(0, 6);
}

export function ProjectCreateDialog({ slug, open, onOpenChange }: { slug: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const nameId = useId();
  const [name, setName] = useState("");
  const [appearance, setAppearance] = useState<{ icon: string | null; color: string }>({ icon: DEFAULT_PROJECT_ICON, color: DEFAULT_PROJECT_COLOR });
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const create = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/workspaces/{slug}/projects", { params: { path: { slug } }, body: { name: name.trim(), key, visibility, ...appearance } })),
    onSuccess: async (p) => {
      await qc.invalidateQueries({ queryKey: ["projects", slug] });
      onOpenChange(false);
      setName("");
      setKey("");
      setKeyTouched(false);
      setAppearance({ icon: DEFAULT_PROJECT_ICON, color: DEFAULT_PROJECT_COLOR });
      await navigate({ to: "/$ws/project/$key", params: { ws: slug, key: p.key } });
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={next => { if (!create.isPending) onOpenChange(next); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-[18%] z-50 w-[460px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-xl border border-border bg-elevated p-5 shadow-2xl">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-[15px] font-semibold">{t("project.newTitle")}</Dialog.Title>
            <Dialog.Close type="button" disabled={create.isPending} aria-label={t("common.close")} className="rounded p-1 text-muted hover:bg-hover">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="mt-1 text-[12.5px] text-muted">{t("project.newSubtitle")}</Dialog.Description>
          <form
            className="mt-4 flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (create.isPending || !name.trim() || key.length < 2) return;
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5 text-[12px] font-medium text-muted">
              <label htmlFor={nameId}>{t("projectIcons.iconAndName")}</label>
              <div className="flex items-center gap-2.5">
                <ProjectIconPicker value={appearance} projectKey={key} onChange={setAppearance} disabled={create.isPending} />
                <input
                  id={nameId}
                  autoFocus
                  required
                  disabled={create.isPending}
                  className="input h-10 min-w-0 flex-1"
                  value={name}
                  placeholder={t("project.namePlaceholder")}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!keyTouched) setKey(suggestKey(e.target.value));
                  }}
                />
              </div>
            </div>
            <label className="flex flex-col gap-1 text-[12px] font-medium text-muted">
              {t("project.key")}
              <input
                required
                disabled={create.isPending}
                className="input h-9 w-[140px] font-mono uppercase"
                value={key}
                maxLength={6}
                pattern="[A-Z][A-Z0-9]{1,5}"
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
                }}
              />
              <span className="font-normal text-faint">{t("project.keyHint", { key: key || "KEY" })}</span>
            </label>
            <div className="flex flex-col gap-1 text-[12px] font-medium text-muted">
              {t("project.visibility")}
              <div className="flex gap-1 rounded-md bg-sidebar p-0.5">
                {(["public", "private"] as const).map((v) => (
                  <button key={v} type="button" disabled={create.isPending} className={`flex-1 rounded px-2 py-1 text-[12.5px] ${visibility === v ? "bg-elevated text-fg shadow" : "text-muted"}`} onClick={() => setVisibility(v)}>
                    {t(`project.${v}`)}
                  </button>
                ))}
              </div>
              <span className="font-normal text-faint">{t(`project.${visibility}Hint`)}</span>
            </div>
            {create.isError && <p className="text-[12px] text-danger">{errorMessage(create.error)}</p>}
            <div className="mt-1 flex justify-end gap-2">
              <Dialog.Close type="button" disabled={create.isPending} className="btn h-8">{t("common.cancel")}</Dialog.Close>
              <button className="btn btn-primary h-8" type="submit" disabled={!name.trim() || key.length < 2 || create.isPending}>
                {t("common.create")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
